import { Bookmark, ChevronRight, Inbox, MessageSquare } from 'lucide-react';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { MarkdownText } from '@/components/common/MarkdownText';
import type {
  NoteContext,
  NotificationSchema,
  TaskUpdateSummary,
} from '@/services/api/notificationsService';
import {
  useNotificationContextQuery,
} from '@/services/api/notificationsService';
import { useCreateStaffNoteMutation } from '@/services/api/staffNotesService';
import { useCreateTaskUpdateMutation } from '@/services/api/tasksService';
import { TaskUpdateVisibility } from '@/services/types/client-intake-types';

import { ConversationDetail } from './ConversationDetail';
import { NotificationDetailHeader } from './NotificationDetailHeader';
import type { NotificationNavTab } from './NotificationNav';
import { ReplyInput } from './ReplyInput';

interface NotificationDetailProps {
  notificationId: string | null;
  tab: NotificationNavTab;
}

/**
 * Format a timestamp to relative time
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Format time for thread items (shorter format)
 */
const formatThreadTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Determine if this is a task or note mention from title
 */
const getMentionType = (title: string): 'task' | 'note' | null => {
  const lower = title.toLowerCase();
  if (lower.includes('in a task') || lower.includes('task')) return 'task';
  if (lower.includes('in a note') || lower.includes('note')) return 'note';
  return null;
};


/**
 * Context Banner Component - Shows TASK or NOTE with title and view link
 */
const ContextBanner: React.FC<{
  type: 'task' | 'note';
  title: string;
  deepLinkUrl: string | null | undefined;
}> = ({ type, title, deepLinkUrl }) => {
  const label = type === 'task' ? 'TASK:' : 'NOTE:';
  const linkText = type === 'task' ? 'View task' : 'View note';

  return (
    <div className="flex items-center justify-between px-3 py-2.5 bg-gray-100 rounded-lg mb-3">
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="text-[10px] font-bold text-gray-500 uppercase flex-shrink-0">{label}</span>
        <span className="text-[13px] font-semibold text-gray-900 truncate">{title}</span>
      </div>
      {deepLinkUrl && (
        <Link
          to={deepLinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-0.5 text-[12px] font-semibold text-indigo-600 hover:text-indigo-700 flex-shrink-0 ml-3"
        >
          {linkText}
          <ChevronRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
};

/**
 * Thread Item Component - Shows a single update in the conversation
 */
const ThreadItem: React.FC<{
  authorName: string;
  content: string;
  createdAt: string;
  isNew?: boolean;
  isUnread?: boolean;
}> = ({ authorName, content, createdAt, isNew, isUnread }) => {
  const showNewBadge = isNew && isUnread;
  const displayName = authorName || 'Unknown';

  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-[13px] font-bold text-gray-900">{displayName}</span>
        {showNewBadge && (
          <span className="px-1.5 py-0.5 text-[10px] font-bold text-indigo-600 bg-indigo-100 rounded uppercase">
            New
          </span>
        )}
        <span className="text-xs text-gray-500 font-medium ml-auto">
          {formatThreadTime(createdAt)}
        </span>
      </div>
      <MarkdownText
        content={content}
        className="text-sm text-gray-800 font-medium leading-snug"
        plainTextClassName="whitespace-pre-wrap"
        markdownClassName="prose-p:my-0.5 prose-ul:my-0.5 prose-ol:my-0.5 prose-p:text-sm prose-li:text-sm"
      />
    </div>
  );
};

/**
 * Task Updates Thread Component - Shows all task updates
 */
const TaskUpdatesThread: React.FC<{
  updates: TaskUpdateSummary[];
  notificationCreatedAt: string;
  isUnread: boolean;
}> = ({ updates, notificationCreatedAt, isUnread }) => {
  if (!updates || updates.length === 0) return null;

  // Sort by created_at descending (newest first)
  const sortedUpdates = [...updates].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  const notificationTime = new Date(notificationCreatedAt).getTime();

  return (
    <div>
      {sortedUpdates.map((update) => {
        const updateTime = new Date(update.created_at).getTime();
        const isNew = Math.abs(updateTime - notificationTime) < 60000;
        return (
          <ThreadItem
            key={update.id}
            authorName={update.author?.display_name || 'Unknown'}
            content={update.content}
            createdAt={update.created_at}
            isNew={isNew}
            isUnread={isUnread}
          />
        );
      })}
    </div>
  );
};

/**
 * Notes Thread Component - Shows all notes on the matter
 */
const NotesThread: React.FC<{
  notes: NoteContext[];
  mentionedNoteId: string | null;
  isUnread: boolean;
}> = ({ notes, mentionedNoteId, isUnread }) => {
  if (!notes || notes.length === 0) return null;

  // Sort by created_at descending (newest first)
  const sortedNotes = [...notes].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  return (
    <div>
      {sortedNotes.map((note) => {
        const isNew = note.id === mentionedNoteId;
        return (
          <ThreadItem
            key={note.id}
            authorName={note.author?.display_name || 'Unknown'}
            content={note.content}
            createdAt={note.created_at}
            isNew={isNew}
            isUnread={isUnread}
          />
        );
      })}
    </div>
  );
};

/**
 * Get the context banner info
 */
const getContextBannerInfo = (
  notification: NotificationSchema,
  task: { title: string | null } | null,
  note: { title: string } | null,
): { type: 'task' | 'note'; title: string } | null => {
  const mentionType = getMentionType(notification.title);

  if (task) {
    return { type: 'task', title: task.title || 'Untitled Task' };
  }
  if (note) {
    return { type: 'note', title: note.title || 'Untitled Note' };
  }
  // Even if no task/note context, show banner based on title
  if (mentionType === 'task') {
    return { type: 'task', title: 'Task' };
  }
  if (mentionType === 'note') {
    return { type: 'note', title: 'Note' };
  }
  return null;
};

export const NotificationDetail: React.FC<NotificationDetailProps> = ({
  notificationId,
  tab,
}) => {
  const isMessagesTab = tab === 'messages';

  // For Messages tab, notificationId is actually the matter_id
  const { data: context, isLoading } = useNotificationContextQuery(
    { id: notificationId! },
    { skip: !notificationId || isMessagesTab },
  );

  const [createTaskUpdate] = useCreateTaskUpdateMutation();
  const [createStaffNote] = useCreateStaffNoteMutation();

  const task = context?.task ?? null;
  const matter = context?.matter ?? null;
  const canReplyToTask = !!task && !!matter;

  const handleReply = useCallback(
    async (content: string) => {
      if (!matter) return;

      try {
        if (canReplyToTask && task) {
          await createTaskUpdate({
            matterId: matter.id,
            taskId: task.id,
            update: {
              update: content,
              visibility: TaskUpdateVisibility.PRIVATE,
            },
          }).unwrap();
        } else {
          await createStaffNote({
            matterId: matter.id,
            note: { content },
          }).unwrap();
        }
        toast.success('Reply sent');
      } catch {
        toast.error('Failed to send reply');
        throw new Error('Failed to send reply');
      }
    },
    [matter, task, canReplyToTask, createTaskUpdate, createStaffNote],
  );

  // Empty state
  if (!notificationId) {
    const emptyIcon = isMessagesTab ? (
      <MessageSquare className="w-8 h-8 text-gray-500" />
    ) : (
      <Inbox className="w-8 h-8 text-gray-500" />
    );
    const emptyTitle = isMessagesTab ? 'No conversation selected' : 'No notification selected';
    const emptyDescription = isMessagesTab
      ? 'Select a conversation from the list to view messages'
      : 'Select a notification from the list to view details';

    return (
      <div className="h-full flex items-center justify-center bg-gray-50/50">
        <div className="text-center max-w-xs">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
            {emptyIcon}
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-1">{emptyTitle}</h3>
          <p className="text-xs text-gray-500 font-medium">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  // For Messages tab, render the ConversationDetail component
  if (isMessagesTab) {
    return <ConversationDetail matterId={notificationId} />;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  // Error or no context
  if (!context) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 text-gray-500">
        <div className="text-center">
          <p className="text-sm font-medium">Could not load notification details</p>
        </div>
      </div>
    );
  }

  const { notification, note, matter_notes } = context;
  const isUnread = !notification.read_at || notification.marked_unread;
  const isMentionTab = tab === 'mentions';
  const mentionType = getMentionType(notification.title);
  const isMention = isMentionTab || mentionType !== null;

  const canReplyToNote = !task && !!matter;
  const canReply = canReplyToTask || canReplyToNote;

  // Get context banner info
  const bannerInfo = isMention ? getContextBannerInfo(notification, task, note) : null;

  // Determine what content to show
  const hasTaskUpdates = task?.updates && task.updates.length > 0;
  const hasMatterNotes = matter_notes && matter_notes.length > 0;
  const hasDescription = notification.description;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header bar with matter info */}
      <NotificationDetailHeader matter={matter} />

      {/* Content area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Sticky header section - title + banner */}
        <div className="flex-shrink-0 bg-white border-b border-gray-100">
          <div className="max-w-2xl mx-auto px-5 py-3">
            {/* Notification title + time */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <h1 className="text-[13px] font-bold text-gray-900">
                {notification.title}
              </h1>
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                {formatRelativeTime(notification.created_at)}
              </span>
            </div>

            {/* Context Banner for mentions */}
            {bannerInfo && (
              <ContextBanner
                type={bannerInfo.type}
                title={bannerInfo.title}
                deepLinkUrl={notification.deep_link_url}
              />
            )}

            {/* Action buttons for non-mentions OR when there's a deep link */}
            {(!isMention || notification.deep_link_url) && !bannerInfo && (
              <div className="flex items-center gap-2">
                {notification.deep_link_url && (
                  <Link
                    to={notification.deep_link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors"
                  >
                    View Details
                  </Link>
                )}
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Coming soon"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  Save for later
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable thread content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-5 py-3">
            {/* Task conversation thread */}
            {hasTaskUpdates && (
              <TaskUpdatesThread
                updates={task!.updates}
                notificationCreatedAt={notification.created_at}
                isUnread={isUnread}
              />
            )}

            {/* Notes thread - shows all notes on the matter */}
            {hasMatterNotes && !hasTaskUpdates && (
              <NotesThread
                notes={matter_notes!}
                mentionedNoteId={note?.id || null}
                isUnread={isUnread}
              />
            )}

            {/* Show description if no task updates and no notes */}
            {hasDescription && !hasTaskUpdates && !hasMatterNotes && (
              <div className="mb-3">
                <MarkdownText
                  content={notification.description!}
                  className="text-sm text-gray-800 font-medium leading-snug"
                  plainTextClassName="whitespace-pre-wrap"
                  markdownClassName="prose-p:my-0.5 prose-ul:my-0.5 prose-ol:my-0.5 prose-p:text-sm prose-li:text-sm"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reply input */}
      {canReply && (
        <ReplyInput
          key={notificationId}
          onSubmit={handleReply}
          placeholder={
            canReplyToTask ? 'Reply to task...' : 'Reply with a note...'
          }
        />
      )}
    </div>
  );
};
